<script setup lang="ts">
import {
    Button,
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    Input,
    ZhPanel,
    ZhSectionHeading,
} from '@zerohuman/ui'
import { computed, ref } from 'vue'
import { closeEnterprise } from '@/api'
import { ZERO_HUMAN_SUPPORT_EMAIL } from '@/lib/signInError'

/**
 * The Danger zone at the end of `/enterprise` (#72, R1): the one place an owner deletes the enterprise, shown to the
 * owner only. Deleting closes the enterprise at once, cancels its subscription now and erases it after its plan's
 * retention, so it sits behind a confirmation that asks for the enterprise's name typed exactly. On success the
 * browser goes to `/?error=enterprise_closed`, where the person carries on in another enterprise they hold, or takes
 * the sign-in path of somebody with none. A refusal keeps the dialog open and says why.
 */
const props = defineProps<{ enterpriseId: string; name: string }>()

/** One list of what goes, said the same way in the panel and in the dialog. */
const ERASED = 'runs, memory, mail, files and secrets'
/** The retention by plan (D1), said once. */
const RETENTION = '7 days on Free and Starter, 30 days on Company and Scale, 365 days on Enterprise'
const supportHref = `mailto:${ZERO_HUMAN_SUPPORT_EMAIL}`
const FALLBACK_FAILURE = `The enterprise could not be deleted. Try again, or email ${ZERO_HUMAN_SUPPORT_EMAIL}.`

/** The API's refusal codes, in words: a bare code is never shown to the owner. */
const FAILURE_WORDS: Record<string, string> = {
    name_mismatch: 'The name you typed does not match this enterprise.',
    owner_only: 'Only the owner can delete this enterprise.',
    subscription_not_cancelled: 'We could not cancel the subscription, so nothing was deleted. Try again shortly.',
    enterprise_closed: 'This enterprise is already closed.',
}

const open = ref(false)
const typed = ref('')
const busy = ref(false)
const failure = ref<string | null>(null)

/** Exactly the name: no other case, no stray space, nothing short of it, and not the slug. */
const confirmed = computed(() => typed.value === props.name)

function setOpen(next: boolean): void {
    // While the delete is in flight the dialog stays: it cancels the subscription and runs, and may take a moment.
    if (!next && busy.value) {
        return
    }
    open.value = next
    if (!next) {
        typed.value = ''
        failure.value = null
    }
}

/** What a refusal says: the API's sentence when it sent one, words for a bare code, a plain fallback otherwise. */
function failureText(error: unknown): string {
    const message = error instanceof Error ? error.message.trim() : ''
    if (!message) {
        return FALLBACK_FAILURE
    }
    if (/^[a-z0-9_.:-]+$/.test(message)) {
        return FAILURE_WORDS[message] ?? FALLBACK_FAILURE
    }
    return message
}

async function confirm(): Promise<void> {
    if (!confirmed.value || busy.value) {
        return
    }
    busy.value = true
    failure.value = null
    try {
        await closeEnterprise(props.enterpriseId, typed.value)
    } catch (error) {
        failure.value = failureText(error)
        busy.value = false
        return
    }
    // Still busy: the page is leaving, so the button keeps saying so rather than offering a second delete.
    globalThis.location.assign('/?error=enterprise_closed')
}
</script>

<template>
	<ZhPanel
		as="section"
		measure="prose"
		class="space-y-3"
		aria-labelledby="enterprise-danger-zone-heading"
		data-testid="enterprise-danger-zone"
	>
		<ZhSectionHeading id="enterprise-danger-zone-heading">Danger zone</ZhSectionHeading>
		<p class="text-muted-foreground text-sm">
			Deleting {{ name }} closes it now: sign-in to it and every run stop, and its subscription is cancelled at
			once, with no refund. Its {{ ERASED }} are kept for your plan's retention ({{ RETENTION }}), then erased
			for good.
		</p>
		<p class="text-muted-foreground text-sm">
			Until then only Zero Human support can reopen it, without the subscription or the connections it had:
			email <a :href="supportHref" class="text-foreground underline underline-offset-4">{{ ZERO_HUMAN_SUPPORT_EMAIL }}</a>.
			Its address is never given to anyone else.
		</p>
		<Button type="button" variant="destructive" data-testid="enterprise-danger-zone-delete" @click="setOpen(true)">
			Delete enterprise
		</Button>
		<Dialog :open="open" @update:open="setOpen">
			<DialogContent class="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Delete {{ name }}?</DialogTitle>
					<DialogDescription>
						It closes now and its subscription is cancelled at once, with no refund. After your plan's
						retention its {{ ERASED }} are erased and cannot be recovered.
					</DialogDescription>
				</DialogHeader>
				<p class="text-muted-foreground text-sm" data-testid="delete-enterprise-reopen">
					Until then only Zero Human support can reopen it, without the subscription:
					email <a :href="supportHref" class="text-foreground underline underline-offset-4">{{ ZERO_HUMAN_SUPPORT_EMAIL }}</a>.
				</p>
				<label class="block space-y-1 text-sm" for="delete-enterprise-name">
					<span class="text-muted-foreground">Type <strong class="text-foreground">{{ name }}</strong> exactly to confirm</span>
					<span class="block text-muted-foreground text-xs" data-testid="delete-enterprise-name-hint">Case and spaces must match.</span>
					<Input
						id="delete-enterprise-name"
						v-model="typed"
						autocomplete="off"
						spellcheck="false"
						:disabled="busy"
						data-testid="delete-enterprise-name"
					/>
				</label>
				<p v-if="failure" class="text-destructive text-sm" role="alert" data-testid="delete-enterprise-error">
					{{ failure }}
				</p>
				<DialogFooter>
					<Button
						type="button"
						variant="outline"
						:disabled="busy"
						data-testid="delete-enterprise-cancel"
						@click="setOpen(false)"
					>
						Cancel
					</Button>
					<Button
						type="button"
						variant="destructive"
						:disabled="!confirmed || busy"
						:aria-busy="busy"
						data-testid="delete-enterprise-confirm"
						@click="confirm"
					>
						{{ busy ? 'Deleting…' : 'Delete enterprise' }}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	</ZhPanel>
</template>
